import { useEffect } from 'react';
import { arbitrum, arbitrumSepolia } from '@reown/appkit/networks';
import { WagmiAdapter } from '@reown/appkit-adapter-wagmi';
import { createAppKit, useAppKitAccount } from '@reown/appkit/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useReadContract, WagmiProvider } from 'wagmi';

// Config
import {
  META_MASK_WALLET_ID,
  REOWN_PROJECT_ID,
  TRUST_WALLET_ID,
  ENV,
  TGE_ADDRESS,
  abiStorage,
  APP_LINK,
  EXCLUDED_WALLETS,
} from '../../config';

// Hooks
import { useGetAdminsList } from '../../hooks/adminHooks/useGetAdminsList';
import { useAppDispatch } from '../../store';

// Actions
import {
  setIsAdmin,
  setIsReferralManager,
  setLockupPeriod,
  setUnlockTime,
} from '../../store/slices/mainSlice';

const isDev = ENV === 'dev';

const metadata = {
  name: 'Tribe perpetual',
  description: 'Tribe perpetual',
  url: APP_LINK,
  icons: ['https://assets.reown.com/reown-profile-pic.png'],
};

const arbitrumFixed = {
  ...arbitrum,
  rpcUrls: {
    default: {
      http: ['https://arbitrum-one-rpc.publicnode.com'],
    },
  },
};

const networks = isDev ? [arbitrumSepolia] : [arbitrumFixed];

const wagmiAdapter = new WagmiAdapter({
  networks,
  projectId: REOWN_PROJECT_ID,
  ssr: true,
});

export const config = wagmiAdapter.wagmiConfig;

createAppKit({
  adapters: [wagmiAdapter],
  networks,
  metadata,
  projectId: REOWN_PROJECT_ID,
  enableWalletConnect: true,
  enableCoinbase: false,
  allWallets: 'HIDE',
  featuredWalletIds: [TRUST_WALLET_ID, META_MASK_WALLET_ID],
  excludeWalletIds: Object.values(EXCLUDED_WALLETS),
  features: {
    email: false,
    socials: [],
    emailShowWallets: true,
    swaps: false,
    onramp: false,
    send: false,
  },
});

const queryClient = new QueryClient();

const WalletData = () => {
  const dispatch = useAppDispatch();
  const { address } = useAppKitAccount();
  const { adminsList } = useGetAdminsList();
  const { adminsList: referralManagersList } = useGetAdminsList(true);

  useEffect(() => {
    if (adminsList) {
      const isAdmin = adminsList.some(admin => admin.result === address);
      dispatch(setIsAdmin(isAdmin));
    }

    if (referralManagersList) {
      const isRefManager = referralManagersList.some(admin => admin.result === address);
      dispatch(setIsReferralManager(isRefManager));
    }
  }, [address, adminsList, dispatch, referralManagersList]);

  const { data: unlockTime } = useReadContract({
    address: TGE_ADDRESS,
    abi: abiStorage.TGE.abi,
    functionName: 'unlockTime',
  });

  useEffect(() => {
    if (unlockTime && !isNaN(parseInt(unlockTime))) {
      dispatch(setUnlockTime(parseInt(unlockTime) * 1000));
    }
  }, [unlockTime, dispatch]);

  const { data: lockupPeriod } = useReadContract({
    address: TGE_ADDRESS,
    abi: abiStorage.TGE.abi,
    functionName: 'LOCKUP_PERIOD',
  });

  useEffect(() => {
    if (lockupPeriod) {
      dispatch(setLockupPeriod(parseInt(lockupPeriod) * 1000));
    }
  }, [lockupPeriod, dispatch]);

  return null;
};

const AppKitProvider = ({ children }) => {
  return (
    <WagmiProvider config={config}>
      <QueryClientProvider client={queryClient}>
        <WalletData />
        {children}
      </QueryClientProvider>
    </WagmiProvider>
  );
};

export default AppKitProvider;
